// src/components/ui/DateRangePicker/DateRangePicker.jsx
import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import styles from './DateRangePicker.module.css';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];
const DAYS_SHORT = ['Su','Mo','Tu','We','Th','Fr','Sa'];

const today = () => {
  const d = new Date();
  d.setHours(0,0,0,0);
  return d;
};

const startOfWeek = (d) => {
  const date = new Date(d);
  date.setDate(date.getDate() - date.getDay());
  date.setHours(0,0,0,0);
  return date;
};

const endOfWeek = (d) => {
  const date = new Date(d);
  date.setDate(date.getDate() + (6 - date.getDay()));
  date.setHours(0,0,0,0);
  return date;
};

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth   = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

const toYMD = (d) => {
  if (!d) return '';
  const year  = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day   = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const fromYMD = (str) => {
  if (!str) return null;
  const [y,m,d] = str.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  date.setHours(0,0,0,0);
  return date;
};

const formatDisplay = (d) => {
  if (!d) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const isSameDay = (a, b) => a && b && toYMD(a) === toYMD(b);
const isBefore  = (a, b) => a && b && a < b;
const isAfter   = (a, b) => a && b && a > b;
const isBetween = (d, from, to) => d && from && to && d > from && d < to;

// ─── Shortcuts ────────────────────────────────────────────────────────────────

const getShortcuts = () => {
  const t = today();
  const yesterday = new Date(t); yesterday.setDate(t.getDate() - 1);

  return [
    {
      label: 'Today',
      from: toYMD(t),
      to:   toYMD(t),
    },
    {
      label: 'Yesterday',
      from: toYMD(yesterday),
      to:   toYMD(yesterday),
    },
    {
      label: 'This Week',
      from: toYMD(startOfWeek(t)),
      to:   toYMD(endOfWeek(t)),
    },
    {
      label: 'Last Week',
      from: (() => { const d = new Date(t); d.setDate(d.getDate() - 7); return toYMD(startOfWeek(d)); })(),
      to:   (() => { const d = new Date(t); d.setDate(d.getDate() - 7); return toYMD(endOfWeek(d)); })(),
    },
    {
      label: 'This Month',
      from: toYMD(startOfMonth(t)),
      to:   toYMD(endOfMonth(t)),
    },
    {
      label: 'Last Month',
      from: (() => { const d = new Date(t.getFullYear(), t.getMonth() - 1, 1); return toYMD(d); })(),
      to:   (() => { const d = new Date(t.getFullYear(), t.getMonth(), 0);     return toYMD(d); })(),
    },
    {
      label: 'Last 30 Days',
      from: (() => { const d = new Date(t); d.setDate(d.getDate() - 29); return toYMD(d); })(),
      to:   toYMD(t),
    },
    {
      label: 'Last 7 Days',
      from: (() => { const d = new Date(t); d.setDate(d.getDate() - 6); return toYMD(d); })(),
      to:   toYMD(t),
    },
  ];
};

// ─── Month Grid ───────────────────────────────────────────────────────────────

const MonthGrid = ({
  year,
  month,            // 0-based
  selectedFrom,     // Date | null
  selectedTo,       // Date | null
  hoverDate,        // Date | null
  onDayClick,
  onDayHover,
  minDate,
  maxDate,
}) => {
  const firstDay  = new Date(year, month, 1).getDay();   // 0=Sun
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];

  // Leading blanks
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  // Effective range end for hover preview
  const rangeEnd = selectedFrom && !selectedTo && hoverDate
    ? (hoverDate >= selectedFrom ? hoverDate : null)
    : selectedTo;

  return (
    <div className={styles.monthGrid}>
      <div className={styles.monthLabel}>{MONTHS[month]} {year}</div>
      <div className={styles.weekdayRow}>
        {DAYS_SHORT.map(d => <span key={d} className={styles.weekday}>{d}</span>)}
      </div>
      <div className={styles.daysGrid}>
        {cells.map((date, idx) => {
          if (!date) return <span key={`blank-${idx}`} className={styles.blank} />;

          const ymd        = toYMD(date);
          const isStart    = isSameDay(date, selectedFrom);
          const isEnd      = isSameDay(date, rangeEnd);
          const inRange    = isBetween(date, selectedFrom, rangeEnd);
          const isDisabled = (minDate && date < minDate) || (maxDate && date > maxDate);
          const isToday    = isSameDay(date, today());

          const cls = [
            styles.day,
            isStart   ? styles.dayStart   : '',
            isEnd     ? styles.dayEnd     : '',
            inRange   ? styles.dayInRange : '',
            isDisabled? styles.dayDisabled: '',
            isToday   ? styles.dayToday   : '',
          ].filter(Boolean).join(' ');

          return (
            <button
              key={ymd}
              type="button"
              className={cls}
              onClick={() => !isDisabled && onDayClick(date)}
              onMouseEnter={() => onDayHover(date)}
              onMouseLeave={() => onDayHover(null)}
              disabled={isDisabled}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * DateRangePicker
 *
 * Props:
 *   value      : { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }
 *   onChange   : ({ from, to }) => void
 *   placeholder: string
 *   disabled   : boolean
 *   minDate    : 'YYYY-MM-DD'
 *   maxDate    : 'YYYY-MM-DD'
 *   className  : string
 */
const DateRangePicker = ({
  value = {},
  onChange,
  placeholder = 'Select date range',
  disabled = false,
  minDate,
  maxDate,
  className = '',
}) => {
  const fromDate = value.from ? fromYMD(value.from) : null;
  const toDate   = value.to   ? fromYMD(value.to)   : null;

  const [open, setOpen]             = useState(false);
  const [dropdownShift, setDropdownShift] = useState(0);
  const dropdownRef = useRef(null);
  const [selecting, setSelecting]   = useState(null);   // Date | null — first click
  const [hoverDate, setHoverDate]   = useState(null);
  const [leftYear,  setLeftYear]    = useState(() => {
    const d = fromDate || today();
    return d.getFullYear();
  });
  const [leftMonth, setLeftMonth]   = useState(() => {
    const d = fromDate || today();
    return d.getMonth();
  });

  const wrapperRef = useRef(null);
  const SHORTCUTS  = getShortcuts();

  // Right panel is always left + 1 month
  const rightMonth = leftMonth === 11 ? 0  : leftMonth + 1;
  const rightYear  = leftMonth === 11 ? leftYear + 1 : leftYear;

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setSelecting(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Shift the panel left when it would run past the viewport's right edge
  useLayoutEffect(() => {
    if (!open) { setDropdownShift(0); return; }
    const el = dropdownRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Nearest clipping edge: the viewport (minus scrollbar) or a scrolling/overflow-hidden ancestor
    let rightEdge = document.documentElement.clientWidth;
    for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
      if (getComputedStyle(p).overflowX !== 'visible') {
        rightEdge = Math.min(rightEdge, p.getBoundingClientRect().right);
      }
    }
    const naturalLeft = rect.left - dropdownShift;
    const overflow = naturalLeft + rect.width - (rightEdge - 8);
    const shift = overflow > 0 ? -Math.min(overflow, Math.max(0, naturalLeft - 8)) : 0;
    if (shift !== dropdownShift) setDropdownShift(shift);
  }, [open, leftYear, leftMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync calendar position when value changes externally
  useEffect(() => {
    if (fromDate) {
      setLeftYear(fromDate.getFullYear());
      setLeftMonth(fromDate.getMonth());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.from]);

  const prevMonth = () => {
    if (leftMonth === 0) { setLeftMonth(11); setLeftYear(y => y - 1); }
    else                 { setLeftMonth(m => m - 1); }
  };

  const nextMonth = () => {
    if (leftMonth === 11) { setLeftMonth(0); setLeftYear(y => y + 1); }
    else                  { setLeftMonth(m => m + 1); }
  };

  const handleDayClick = useCallback((date) => {
    if (!selecting) {
      // First click — set start
      setSelecting(date);
      onChange({ from: toYMD(date), to: '' });
    } else {
      // Second click — set end (ensure from <= to)
      let from = selecting;
      let to   = date;
      if (isBefore(to, from)) { [from, to] = [to, from]; }
      setSelecting(null);
      setHoverDate(null);
      onChange({ from: toYMD(from), to: toYMD(to) });
      setOpen(false);
    }
  }, [selecting, onChange]);

  const handleShortcut = (shortcut) => {
    setSelecting(null);
    setHoverDate(null);
    onChange({ from: shortcut.from, to: shortcut.to });
    // Navigate calendar to start of the shortcut range
    const d = fromYMD(shortcut.from);
    setLeftYear(d.getFullYear());
    setLeftMonth(d.getMonth());
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelecting(null);
    setHoverDate(null);
    onChange({ from: '', to: '' });
  };

  const displayValue = () => {
    if (selecting) return `${formatDisplay(selecting)} – ...`;
    if (fromDate && toDate) {
      if (isSameDay(fromDate, toDate)) return formatDisplay(fromDate);
      return `${formatDisplay(fromDate)} – ${formatDisplay(toDate)}`;
    }
    if (fromDate) return `${formatDisplay(fromDate)} – ...`;
    return '';
  };

  const hasValue = !!(value.from || value.to);
  const minDateObj = minDate ? fromYMD(minDate) : null;
  const maxDateObj = maxDate ? fromYMD(maxDate) : null;

  // Which shortcut is currently active?
  const activeShortcut = SHORTCUTS.find(s => s.from === value.from && s.to === value.to);

  return (
    <div
      ref={wrapperRef}
      className={`${styles.wrapper} ${className} ${disabled ? styles.disabled : ''}`}
    >
      {/* Trigger */}
      <div
        className={`${styles.trigger} ${open ? styles.triggerOpen : ''}`}
        onClick={() => !disabled && setOpen(o => !o)}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); !disabled && setOpen(o => !o); } }}
      >
        <Calendar className={styles.triggerIcon} size={16} />
        <span className={`${styles.triggerText} ${!hasValue && !selecting ? styles.placeholder : ''}`}>
          {displayValue() || placeholder}
        </span>
        {hasValue && (
          <button
            type="button"
            className={styles.clearButton}
            onClick={handleClear}
            title="Clear"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div ref={dropdownRef} className={styles.dropdown} style={dropdownShift ? { left: dropdownShift } : undefined}>
          {/* Shortcuts sidebar */}
          <div className={styles.shortcuts}>
            {SHORTCUTS.map(s => (
              <button
                key={s.label}
                type="button"
                className={`${styles.shortcut} ${activeShortcut?.label === s.label ? styles.shortcutActive : ''}`}
                onClick={() => handleShortcut(s)}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Calendar area */}
          <div className={styles.calendarArea}>
            {/* Nav bar */}
            <div className={styles.navBar}>
              <button type="button" className={styles.navBtn} onClick={prevMonth}>
                <ChevronLeft size={16} />
              </button>
              <span className={styles.navSpacer} />
              <button type="button" className={styles.navBtn} onClick={nextMonth}>
                <ChevronRight size={16} />
              </button>
            </div>

            {/* Two month grids */}
            <div className={styles.gridsRow}>
              <MonthGrid
                year={leftYear}
                month={leftMonth}
                selectedFrom={selecting || fromDate}
                selectedTo={selecting ? null : toDate}
                hoverDate={hoverDate}
                onDayClick={handleDayClick}
                onDayHover={setHoverDate}
                minDate={minDateObj}
                maxDate={maxDateObj}
              />
              <div className={styles.gridDivider} />
              <MonthGrid
                year={rightYear}
                month={rightMonth}
                selectedFrom={selecting || fromDate}
                selectedTo={selecting ? null : toDate}
                hoverDate={hoverDate}
                onDayClick={handleDayClick}
                onDayHover={setHoverDate}
                minDate={minDateObj}
                maxDate={maxDateObj}
              />
            </div>

            {/* Footer hint */}
            <div className={styles.footer}>
              {selecting
                ? 'Click a second date to complete the range'
                : hasValue
                ? 'Click the calendar to change the range'
                : 'Click a start date'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;